import React, { useState } from 'react';
import classNames from 'classnames';

// Utils
import { FormattedMessage, useIntl } from '../../../util/reactIntl';

// Shared components
import { NamedLink, PrimaryButton } from '../../../components';
import SubscriptionModal from '../../../components/SubscriptionModal/SubscriptionModal';

import css from './SectionJobDetails.module.css';

/** Format "Posted X hours ago" */
const formatPostedAgo = (createdAt, intl) => {
  if (!createdAt) return null;
  const diffMins = Math.floor((Date.now() - new Date(createdAt)) / 60000);
  if (diffMins < 60) {
    const count = Math.max(1, diffMins);
    return intl.formatMessage({ id: 'SectionJobDetails.postedMinutesAgo' }, { count });
  }
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24)
    return intl.formatMessage({ id: 'SectionJobDetails.postedHoursAgo' }, { count: diffHours });
  return intl.formatMessage(
    { id: 'SectionJobDetails.postedDaysAgo' },
    { count: Math.floor(diffHours / 24) }
  );
};

/** Extract "City, ST" from a full address string */
const extractCityState = address => {
  if (!address) return null;
  const parts = address.split(',').map(p => p.trim());
  if (parts.length < 2) return address;
  const clean = parts.map(p => p.replace(/\s\d{5}(-\d{4})?$/, '').trim());
  const stateIdx = clean.findIndex(p => /^[A-Z]{2}$/.test(p));
  if (stateIdx >= 1) return `${clean[stateIdx - 1]}, ${clean[stateIdx]}`;
  const COUNTRIES = new Set(['United States', 'USA', 'US', 'Canada', 'United Kingdom', 'UK', 'India']);
  const filtered = clean.filter(p => !COUNTRIES.has(p));
  if (filtered.length >= 2) return `${filtered[filtered.length - 2]}, ${filtered[filtered.length - 1]}`;
  return filtered[0] || address;
};

/** A single label + value row */
const JobDetailRow = ({ icon, labelId, value }) => {
  if (!value) return null;
  return (
    <div className={css.detailRow}>
      <dt className={css.detailLabel}>
        {icon && <span className={css.detailIcon}>{icon}</span>}
        <FormattedMessage id={labelId} />
      </dt>
      <dd className={css.detailValue}>{value}</dd>
    </div>
  );
};

/** Progress bar for filled positions */
const SpotsProgress = ({ spotsFilled, spotsRequired }) => {
  const rawFilled = Number(spotsFilled) || 0;
  const total = Number(spotsRequired) || 0;
  if (!total) return null;

  // Cap filled at total so the bar and counts never exceed 100 % / the required number
  const filled = Math.min(rawFilled, total);
  const isFull = filled >= total;
  const available = Math.max(0, total - filled);
  const percentage = Math.min(Math.round((filled / total) * 100), 100);

  return (
    <div className={css.spotsContainer}>
      <div className={css.spotsHeader}>
        <span className={css.spotsText}>
          {isFull ? (
            <FormattedMessage id="SectionJobDetails.positionsFull" />
          ) : (
            <FormattedMessage
              id="SectionJobDetails.spotsAvailable"
              values={{ count: available }}
            />
          )}
        </span>
        <span className={css.spotsSubText}>
          <FormattedMessage id="SectionJobDetails.positionsFilled" values={{ filled, total }} />
        </span>
      </div>
      <div className={css.progressBar}>
        <div
          className={classNames(css.progressFill, { [css.progressFull]: isFull })}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

/**
 * Renders job-specific details panel on the ListingPage for 'job' listing types.
 */
const SectionJobDetails = props => {
  const {
    publicData,
    listing,
    params,
    isOwnListing,
    isClosed,
    existingJobApplicationId,
    applicantStats,
    currentUser,
  } = props;
  const intl = useIntl();
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);

  const userPublicData = currentUser?.attributes?.profile?.publicData || {};
  const isSubscriptionActive = userPublicData.subscriptionStatus === 'active' || userPublicData.subscriptionStatus === 'cancelling';
  const isFoundingMember = userPublicData.isFoundingMember === true;
  const hasReceivedFirstInquiry = userPublicData.hasReceivedFirstInquiry === true;
  // When not logged in, eligible is treated as false so we redirect to login,
  // bringing the user back to the LISTING page (not directly to the apply page).
  // The subscription gate in RequestQuotePage.js provides a second check after login.
  const isEligibleToApply =
    isSubscriptionActive || (isFoundingMember && !hasReceivedFirstInquiry);

  const createdAt = listing?.attributes?.createdAt;

  const {
    event_start_date,
    event_end_date,
    spots_required,
    spots_filled = 0,
    estimated_hours,
    experience_required,
    job_category,
    position_type,
    employment_type,
    pay_type,
    pay_min,
    pay_max,
    pay_amount, // legacy single-amount — kept for backwards compatibility
    applicant_location_preference,
    travel_expenses,
    location,
    studio_location,
    urgent,
  } = publicData || {};

  const requiredCount = Number(spots_required) || 0;
  // Prefer applicantStats.hired (real hired count) over publicData.spots_filled which
  // can include pending transactions and become stale.
  const rawFilled =
    applicantStats?.hired != null ? applicantStats.hired : Number(spots_filled) || 0;
  // Cap filledCount so it never exceeds requiredCount in any display
  const filledCount = requiredCount > 0 ? Math.min(rawFilled, requiredCount) : rawFilled;
  const isFullyBooked =
    publicData?.job_status === 'filled' || (requiredCount > 0 && filledCount >= requiredCount);
  const isArchived = publicData?.job_status === 'archived';
  const hasAlreadyApplied = !!existingJobApplicationId;
  const canApply =
    !isOwnListing && !isClosed && !isFullyBooked && !isArchived && !hasAlreadyApplied;

  /* ── Event dates ── */
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const parseDateParts = iso => {
    if (!iso) return null;
    const d = new Date(iso);
    return { year: d.getFullYear(), month: d.getMonth(), day: d.getDate() };
  };
  const formatEventDates = (start, end) => {
    if (!start) return null;
    const s = parseDateParts(start);
    if (!s) return null;
    if (!end || start === end) return `${MONTHS[s.month]} ${s.day}, ${s.year}`;
    const e = parseDateParts(end);
    if (!e) return `${MONTHS[s.month]} ${s.day}, ${s.year}`;
    if (s.month === e.month && s.year === e.year) {
      return `${MONTHS[s.month]} ${s.day} – ${e.day}, ${e.year}`;
    }
    return `${MONTHS[s.month]} ${s.day} – ${MONTHS[e.month]} ${e.day}, ${e.year}`;
  };
  const eventDatesText = formatEventDates(event_start_date, event_end_date);

  /* ── Category ── */
  const CATEGORY_LABELS = {
    wedding: intl.formatMessage({ id: 'SectionJobDetails.categoryWedding' }),
    studio: intl.formatMessage({ id: 'SectionJobDetails.categoryStudio' }),
    retail_shop: intl.formatMessage({ id: 'SectionJobDetails.categoryRetailShop' }),
    delivery: intl.formatMessage({ id: 'SectionJobDetails.categoryDelivery' }),
    install: intl.formatMessage({ id: 'SectionJobDetails.categoryInstall' }),
    breakdown: intl.formatMessage({ id: 'SectionJobDetails.categoryBreakdown' }),
    freelance: intl.formatMessage({ id: 'SectionJobDetails.categoryFreelance' }),
  };
  const categoryLabel = job_category ? CATEGORY_LABELS[job_category] || job_category : null;

  const POSITION_TYPE_LABELS = {
    event_gig: intl.formatMessage({ id: 'SectionJobDetails.positionTypeEventGig' }),
    seasonal: intl.formatMessage({ id: 'SectionJobDetails.positionTypeSeasonal' }),
    temporary: intl.formatMessage({ id: 'SectionJobDetails.positionTypeTemporary' }),
    part_time: intl.formatMessage({ id: 'SectionJobDetails.positionTypePartTime' }),
    full_time: intl.formatMessage({ id: 'SectionJobDetails.positionTypeFullTime' }),
    permanent: intl.formatMessage({ id: 'SectionJobDetails.positionTypePermanent' }),
    // backwards compat
    holiday: intl.formatMessage({ id: 'SectionJobDetails.positionTypeSeasonal' }),
  };
  const positionTypeLabel = position_type
    ? POSITION_TYPE_LABELS[position_type] || position_type
    : null;

  const EMPLOYMENT_TYPE_LABELS = {
    employee_w2: intl.formatMessage({ id: 'SectionJobDetails.employmentTypeW2' }),
    contractor_1099: intl.formatMessage({ id: 'SectionJobDetails.employmentType1099' }),
  };
  const employmentTypeLabel = employment_type
    ? EMPLOYMENT_TYPE_LABELS[employment_type] || employment_type
    : null;

  /* ── Pay range ── */
  const suffix = pay_type === 'hourly' ? ' / hr' : pay_type === 'flat_rate' ? ' flat' : '';
  const payText =
    pay_type === 'negotiable'
      ? intl.formatMessage({ id: 'SectionJobDetails.payNegotiable' })
      : pay_min != null && pay_max != null
        ? `$${pay_min}–$${pay_max}${suffix}`
        : pay_min != null
          ? `$${pay_min}+${suffix}`
          : pay_amount != null
            ? `$${pay_amount}${suffix}` // backwards compat with old single-amount listings
            : null;

  /* ── Applicant location / travel (event gigs only) ── */
  const isEventPosition = position_type === 'event_gig' || position_type === 'seasonal';

  const APPLICANT_LOCATION_LABELS = {
    local_only: intl.formatMessage({ id: 'SectionJobDetails.applicantLocationLocalOnly' }),
    local_preferred: intl.formatMessage({ id: 'SectionJobDetails.applicantLocationLocalPreferred' }),
    open_to_all: intl.formatMessage({ id: 'SectionJobDetails.applicantLocationOpenToAll' }),
  };
  const applicantLocationText = isEventPosition && applicant_location_preference
    ? APPLICANT_LOCATION_LABELS[applicant_location_preference] || applicant_location_preference
    : null;

  const TRAVEL_EXPENSE_LABELS = {
    none: intl.formatMessage({ id: 'SectionJobDetails.travelExpenseNone' }),
    mileage: intl.formatMessage({ id: 'SectionJobDetails.travelExpenseMileage' }),
    airfare: intl.formatMessage({ id: 'SectionJobDetails.travelExpenseAirfare' }),
    hotel: intl.formatMessage({ id: 'SectionJobDetails.travelExpenseHotel' }),
    parking: intl.formatMessage({ id: 'SectionJobDetails.travelExpenseParking' }),
    negotiable: intl.formatMessage({ id: 'SectionJobDetails.travelExpenseNegotiable' }),
    other: intl.formatMessage({ id: 'SectionJobDetails.travelExpenseOther' }),
    // legacy multi-select values
    meals: intl.formatMessage({ id: 'SectionJobDetails.travelExpenseMeals' }),
  };
  const travelExpensesText = isEventPosition && travel_expenses
    ? Array.isArray(travel_expenses)
      ? travel_expenses.map(k => TRAVEL_EXPENSE_LABELS[k] || k).join(', ')
      : TRAVEL_EXPENSE_LABELS[travel_expenses] || travel_expenses
    : null;

  /* ── Experience ── */
  const EXP_LABELS = {
    entry: intl.formatMessage({ id: 'SectionJobDetails.experience.entry' }),
    intermediate: intl.formatMessage({ id: 'SectionJobDetails.experience.intermediate' }),
    experienced: intl.formatMessage({ id: 'SectionJobDetails.experience.experienced' }),
  };
  const experienceLabel = experience_required ? EXP_LABELS[experience_required] : null;

  const estimatedHoursText = estimated_hours
    ? intl.formatMessage({ id: 'SectionJobDetails.estimatedHoursValue' }, { hours: estimated_hours })
    : null;

  /* ── Work schedule ── */
  const job_schedule = publicData?.job_schedule || [];
  const WEEKDAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const formatScheduleDate = iso => {
    if (!iso) return null;
    const [y, m, d] = iso.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return `${WEEKDAYS[date.getDay()]}, ${MONTHS[date.getMonth()]} ${date.getDate()}`;
  };

  /* ── Location & time ── */
  const cityState = extractCityState(location?.address);
  const postedAgo = formatPostedAgo(createdAt, intl);

  return (
    <div className={css.root}>
      {/* ── Header ── */}
      <div className={css.header}>
        <h2 className={css.heading}>
          <FormattedMessage id="SectionJobDetails.heading" />
        </h2>
        <div className={css.headerBadges}>
          {urgent ? (
            <span className={css.urgentBadge}>
              🔥 <FormattedMessage id="SectionJobDetails.urgentLabel" />
            </span>
          ) : null}
          {positionTypeLabel ? (
            <span className={css.categoryBadge}>{positionTypeLabel}</span>
          ) : null}
          {categoryLabel && !positionTypeLabel ? (
            <span className={css.categoryBadge}>{categoryLabel}</span>
          ) : null}
        </div>
      </div>

      {/* ── Job listing title ── */}
      {listing?.attributes?.title && (
        <p className={css.listingTitle}>{listing.attributes.title}</p>
      )}

      {/* ── Meta chips: location + posted time ── */}
      {(cityState || postedAgo) && (
        <div className={css.metaChips}>
          {cityState && (
            <span className={css.metaChip}>
              <span className={css.chipIcon}>📍</span>
              {cityState}
            </span>
          )}
          {postedAgo && (
            <span className={css.metaChip}>
              <span className={css.chipIcon}>🕒</span>
              {postedAgo}
            </span>
          )}
        </div>
      )}

      {/* ── Spots progress ── */}
      <SpotsProgress spotsFilled={filledCount} spotsRequired={requiredCount} />

      {/* ── Applicants dashboard (florist / own listing only) ── */}
      {isOwnListing && (
        <div className={css.applicantsDashboard}>
          <p className={css.applicantsHeading}>
            <FormattedMessage id="SectionJobDetails.applicantsHeading" />
          </p>
          <div className={css.applicantsStatsRow}>
            <div className={css.applicantsStat}>
              <span className={css.applicantsStatValue}>
                {applicantStats?.total ?? 0}
              </span>
              <span className={css.applicantsStatLabel}>
                <FormattedMessage id="SectionJobDetails.applicantsTotal" />
              </span>
            </div>
            <div className={css.applicantsStat}>
              <span className={css.applicantsStatValue}>
                {applicantStats?.hired ?? 0}
              </span>
              <span className={css.applicantsStatLabel}>
                <FormattedMessage id="SectionJobDetails.applicantsHired" />
              </span>
            </div>
            <div className={css.applicantsStat}>
              <span className={css.applicantsStatValue}>
                {applicantStats?.pending ?? 0}
              </span>
              <span className={css.applicantsStatLabel}>
                <FormattedMessage id="SectionJobDetails.applicantsPending" />
              </span>
            </div>
          </div>
          <NamedLink name="InboxPage" params={{ tab: 'sales' }} className={css.viewApplicantsLink}>
            <FormattedMessage id="SectionJobDetails.viewApplicantsLink" />
          </NamedLink>
        </div>
      )}

      {/* ── Pay block ── */}
      {payText && (
        <div className={css.payBlock}>
          <div className={css.payRow}>
            <span className={css.payLabel}>
              <FormattedMessage id="SectionJobDetails.payRange" />
            </span>
            <span className={css.payValue}>{payText}</span>
          </div>
          <span className={css.offlineBadge}>
            <FormattedMessage id="SectionJobDetails.offlinePaymentNote" />
          </span>
        </div>
      )}

      {/* ── Core detail rows ── */}
      <dl className={css.detailsList}>
        <JobDetailRow icon="🏷️" labelId="SectionJobDetails.positionType" value={positionTypeLabel} />
        <JobDetailRow icon="📋" labelId="SectionJobDetails.employmentType" value={employmentTypeLabel} />
        <JobDetailRow icon="📅" labelId="SectionJobDetails.eventDates" value={eventDatesText} />
        <JobDetailRow icon="👥" labelId="SectionJobDetails.spotsRequired" value={spots_required != null ? String(spots_required) : null} />
        <JobDetailRow icon="⏱" labelId="SectionJobDetails.estimatedHours" value={estimatedHoursText} />
        <JobDetailRow icon="⭐" labelId="SectionJobDetails.experienceRequired" value={experienceLabel} />
        <JobDetailRow icon="🏢" labelId="SectionJobDetails.studioLocation" value={studio_location?.address || null} />
        {isEventPosition && (
          <>
            <JobDetailRow icon="📍" labelId="SectionJobDetails.applicantLocation" value={applicantLocationText} />
            <JobDetailRow icon="✈️" labelId="SectionJobDetails.travelExpenses" value={travelExpensesText} />
          </>
        )}
      </dl>

      {/* ── Work schedule ── */}
      {job_schedule.length > 0 && (
        <div className={css.scheduleSection}>
          <h4 className={css.scheduleSectionTitle}>
            <FormattedMessage id="SectionJobDetails.workScheduleTitle" />
          </h4>
          {job_schedule.map((entry, i) => (
            <div key={i} className={css.scheduleEntry}>
              <div className={css.scheduleEntryHeader}>
                <span className={css.scheduleEntryDate}>
                  📅 {formatScheduleDate(entry.date) || <FormattedMessage id="SectionJobDetails.scheduleNoDate" />}
                </span>
                {(entry.startTime || entry.endTime) && (
                  <span className={css.scheduleEntryTime}>
                    {entry.startTime}{entry.startTime && entry.endTime ? ' – ' : ''}{entry.endTime}
                  </span>
                )}
              </div>
              {(entry.locationType || entry.address) && (
                <div className={css.scheduleEntryLocation}>
                  {entry.locationType && (
                    <div className={css.scheduleLocationType}>
                      <span>📍</span>
                      <span>{entry.locationType}</span>
                    </div>
                  )}
                  {entry.address && (
                    <div className={css.scheduleLocationAddress}>{entry.address}</div>
                  )}
                  {entry.addressLine2 && (
                    <div className={css.scheduleLocationAddress}>{entry.addressLine2}</div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── CTA ── */}
      {canApply ? (
        !currentUser ? (
          // Not logged in — redirect to login, return here (not to apply page)
          <NamedLink
            name="LoginPage"
            to={{ state: { from: `${typeof window !== 'undefined' ? window.location.pathname : ''}` } }}
            className={css.ctaLink}
          >
            <PrimaryButton className={css.ctaButton}>
              <FormattedMessage id="SectionJobDetails.applyButton" />
            </PrimaryButton>
          </NamedLink>
        ) : isEligibleToApply ? (
          <NamedLink
            name="RequestQuotePage"
            params={{ slug: params.slug, id: params.id }}
            className={css.ctaLink}
          >
            <PrimaryButton className={css.ctaButton}>
              <FormattedMessage id="SectionJobDetails.applyButton" />
            </PrimaryButton>
          </NamedLink>
        ) : (
          <>
            <PrimaryButton
              className={css.ctaButton}
              onClick={() => setSubscriptionModalOpen(true)}
            >
              <FormattedMessage id="SectionJobDetails.applyButton" />
            </PrimaryButton>
            <SubscriptionModal
              isOpen={subscriptionModalOpen}
              onClose={() => setSubscriptionModalOpen(false)}
              title="Membership Required to Apply for Jobs"
              subtitle="An active FleurSpace Membership is required for freelancers to submit job applications and connect with clients."
              context="freelancer"
            />
          </>
        )
      ) : hasAlreadyApplied ? (
        <NamedLink
          name="OrderDetailsPage"
          params={{ id: existingJobApplicationId }}
          className={css.ctaLink}
        >
          <PrimaryButton className={css.ctaButton}>
            <FormattedMessage id="SectionJobDetails.viewApplicationButton" />
          </PrimaryButton>
        </NamedLink>
      ) : isOwnListing ? (
        <p className={css.ownListingNote}>
          <FormattedMessage id="SectionJobDetails.ownListingNote" />
        </p>
      ) : isArchived ? (
        <p className={css.closedNote}>
          <FormattedMessage id="SectionJobDetails.positionArchived" />
        </p>
      ) : isFullyBooked ? (
        <p className={css.closedNote}>
          <FormattedMessage id="SectionJobDetails.positionFilled" />
        </p>
      ) : null}
    </div>
  );
};

export default SectionJobDetails;
